import Button from '@carvana/ds-button';
import { icon } from '@carvana/ds-styles';
import { CircleAlert } from 'lucide-react';
import React, { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventedModal, ModalNames } from '|/app/modals';
import { rollBack } from '|/app/services';
import { FormStateContext, useSlate } from '|/context';
import { isNativeWebview } from '|/helpers/is-native-app';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events';
import sendNativeEvent from '|/helpers/send-native-event';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import { clearScopedSlotPreference } from '|/hooks/use-slot-preference';
import useSubmit from '|/hooks/use-submit';
import styles from './edit-order-modal.module.css';

/** Props for the edit-order confirmation modal. */
interface EditOrderModalDSProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Unnormalized `summary.transfer.deliveryType`. Omit or pass `null` when unknown and the
   * copy falls back to a generic "appointment".
   */
  deliveryPreference?: string | null;
  /** Section the customer chose to edit, used as the analytics target on both buttons. */
  editingSectionName?: string;
  /** Step to return to once the rollback resolves, as a `/steps/<id>` id. */
  targetStep?: string;
  /** Section the customer is on now. `'Finalize Purchase'` emits its own analytics event. */
  currentSectionName?: string;
}

/** Noun used for the transfer in edit-order copy. `'appointment'` is the unknown-kind fallback. */
type AppointmentKind = 'pickup' | 'delivery' | 'appointment';

/**
 * Picks the copy noun for an order's transfer.
 *
 * Case-insensitive substring match, so variant-prefixed types land on the same noun;
 * `pickup` is tested before `delivery`. Anything absent or unrecognized returns
 * `'appointment'` so copy stays truthful instead of naming a kind the order hasn't confirmed.
 *
 * @param deliveryType Unnormalized `summary.transfer.deliveryType`.
 */
const resolveAppointmentKind = (deliveryType?: string | null): AppointmentKind => {
  const value = deliveryType?.toLowerCase() ?? '';
  if (value.includes('pickup')) return 'pickup';
  if (value.includes('delivery')) return 'delivery';
  return 'appointment';
};

const EditOrderModal = ({
  isOpen,
  onClose,
  deliveryPreference = null,
  editingSectionName = '',
  targetStep,
  currentSectionName
}: EditOrderModalDSProps) => {
  const { trackEvent } = useAnalyticsContext();
  const { isSubmitting } = useContext(FormStateContext);
  const { isSlateVehicle } = useSlate();
  const submit = useSubmit();
  const navigate = useNavigate();

  const [, , , route] = window.location.pathname.split('/');

  const appointmentKind = resolveAppointmentKind(deliveryPreference);
  const appointmentPhrase =
    appointmentKind === 'appointment' ? appointmentKind : `${appointmentKind} appointment`;

  const isOrderPlaced = route === 'order-placed';

  const handleConfirmEdit = useCallback(() => {
    if (isOrderPlaced) {
      trackEvent('v2_opd_edit_order_button_tap');
    }

    if (currentSectionName === 'Finalize Purchase') {
      trackEvent('v2_finalize_purchase_edit_button_clicked');
    }

    const abortController = new AbortController();

    submit(
      rollBack({
        cancelTransfer: true
      }).then(() => {
        clearScopedSlotPreference();

        // Fires from the rollback continuation, not the tap: native relaunches
        // FullPurchaseWebView on receipt, so the rollback must land first. Returns without
        // navigating — this webview is native's OPD surface, and routing it into the purchase
        // flow would take it outside OPD's boundaries. Source is 'opd', not this repo's usual
        // 'purchase' — that's what native matches on. Closes the modal because native may keep
        // this webview alive; leaving it open lets the customer re-confirm and emit again.
        if (isOrderPlaced && isNativeWebview()) {
          sendNativeEvent({
            event: MobileWebviewEvents.EDIT_ORDER,
            source: 'opd'
          });
          onClose();
          return;
        }

        if (targetStep) {
          const returnUrl = encodeURIComponent(`/steps/${targetStep}`);
          navigate(`/next-step?returnUrl=${returnUrl}`, { replace: true });
          return;
        }

        navigate('/purchase', { replace: true });
      }),
      abortController.signal
    );
  }, [submit, trackEvent, currentSectionName, targetStep, navigate, isOrderPlaced, onClose]);

  const handleModalClose = useCallback(() => {
    trackEvent('pp_edit_order_modal_closed');
    onClose();
  }, [onClose, trackEvent]);

  const handleGoBack = useCallback(() => {
    trackEvent('pp_edit_order_go_back');
    onClose();
  }, [onClose, trackEvent]);

  const renderAlertHeader = () => {
    const defaultHeaderCopy = 'Are you sure you want to edit your order?';

    switch (true) {
      case isOrderPlaced && isSlateVehicle:
        return defaultHeaderCopy;
      case isOrderPlaced:
        return `This will release your ${appointmentKind} date`;
      default:
        return defaultHeaderCopy;
    }
  };

  const renderAlertDescription = () => {
    switch (true) {
      case isOrderPlaced && isSlateVehicle:
        return `Once your changes are saved, you will choose a new date and time for your ${appointmentKind}`;
      case isOrderPlaced:
        return (
          <>
            {`Editing your order releases your ${appointmentPhrase}. Schedule a new one before the timer runs out, or `}
            <strong>{`your vehicle will become available for others to buy`}</strong>
          </>
        );
      default:
        return 'You will have a limited time to make your changes, otherwise your vehicle will become unlocked and available for others to purchase.';
    }
  };

  const renderConfirmButtonCopy = () => {
    switch (true) {
      case isOrderPlaced && isSlateVehicle:
        return 'Edit order';
      case isOrderPlaced:
        return appointmentKind === 'appointment'
          ? 'Release appointment'
          : `Release ${appointmentKind} date`;
      default:
        return 'Yes, edit';
    }
  };

  const renderBackButtonCopy = () => {
    const defaultBackButtonCopy = 'No, go back';

    switch (true) {
      case isOrderPlaced && isSlateVehicle:
        return defaultBackButtonCopy;
      case isOrderPlaced:
        return 'Go back';
      default:
        return defaultBackButtonCopy;
    }
  };

  return (
    <EventedModal
      isOpen={isOpen}
      analyticsId={ModalNames.EditOrder}
      modalHeader="Edit order"
      onClose={handleModalClose}
      desktopWidth="480px"
      mobileStyle="inset"
      classes={{ body: styles.editOrderModalBody }}
    >
      <div className={styles.content}>
        <div className={styles.alertSection}>
          <div className={styles.alertHeader}>
            <CircleAlert className={styles.alertIcon} color={icon.warning} />
            <h3 className={styles.alertTitle}>{renderAlertHeader()}</h3>
          </div>
          <p className={styles.alertDescription}>{renderAlertDescription()}</p>
        </div>
        <div className={styles.footer}>
          <Button
            type="button"
            variant="tertiary"
            size="md"
            onClick={handleConfirmEdit}
            disabled={isSubmitting}
            isLoading={isSubmitting}
            data-analytics-id="pp_edit_modal_edit"
            data-analytics-attributes={JSON.stringify({
              target: editingSectionName.toLowerCase().replace(/\s+/g, '_')
            })}
          >
            {renderConfirmButtonCopy()}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="md"
            className={styles.primaryAction}
            onClick={handleGoBack}
            disabled={isSubmitting}
            data-analytics-id="pp_edit_modal_cancel"
            data-analytics-attributes={JSON.stringify({
              target: editingSectionName.toLowerCase().replace(/\s+/g, '_')
            })}
          >
            {renderBackButtonCopy()}
          </Button>
        </div>
      </div>
    </EventedModal>
  );
};

export default EditOrderModal;
